import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button, Input, Space, message, Modal, Typography
} from 'antd';
import {
  DragOutlined, DeleteOutlined, EditOutlined,
  CopyOutlined, EyeOutlined, AppstoreAddOutlined,
  ThunderboltFilled, CodeFilled, SafetyCertificateFilled
} from '@ant-design/icons';
import styled from 'styled-components';

const { TextArea } = Input;
const { Text, Title } = Typography;

// Section Library - Pre-built prompt sections
const SECTION_LIBRARY = {
  systemRole: {
    id: 'system_role',
    type: 'systemRole',
    name: 'Intelligence Persona',
    description: 'Define the AI advisor background',
    content: 'You are an elite strategic advisor specializing in multi-dimensional data synthesis.',
    icon: '🧠',
    color: '#3a86ff'
  },
  analysisGuidelines: {
    id: 'analysis_guidelines',
    type: 'guidelines',
    name: 'Strategic Directives',
    description: 'Core logic principles',
    content: ['Maintain high precision', 'Context-aware synthesis', 'Actionable intelligence'],
    icon: '🎯',
    color: '#06d6a0'
  },
  outputFormat: {
    id: 'output_format',
    type: 'format',
    name: 'Visual Structure',
    description: 'Define reporting layout',
    content: { structure: 'executive_brief', tone: 'precise', includeVisuals: true },
    icon: '📊',
    color: '#ff9f1c'
  },
  constraints: {
    id: 'constraints',
    type: 'constraints',
    name: 'Guardrails',
    description: 'Execution boundaries',
    content: 'Avoid hallucinations. Cite specific data anchors.',
    icon: '🛡️',
    color: '#ef476f'
  }
};

// Sortable Section Component
const SortableSection = ({ section, onEdit, onDelete, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 1
  };

  return (
    <SectionWrapper ref={setNodeRef} style={style} $isDragging={isDragging}>
      <DragHandle {...attributes} {...listeners}>
        <DragOutlined />
      </DragHandle>
      
      <SectionCardContent>
        <SectionBadge style={{ backgroundColor: section.color + '15', color: section.color }}>
          {section.icon}
        </SectionBadge>
        
        <SectionMain>
          <SectionHeaderRow>
            <div>
              <SectionTitleText>{section.name}</SectionTitleText>
              <SectionSubtext>{section.description}</SectionSubtext>
            </div>
            <ActionIcons size="small">
              <IconButton icon={<EditOutlined />} onClick={() => onEdit(section)} />
              <IconButton icon={<CopyOutlined />} onClick={() => onDuplicate(section)} />
              <IconButton icon={<DeleteOutlined />} danger onClick={() => onDelete(section.id)} />
            </ActionIcons>
          </SectionHeaderRow>
          
          <PreviewSnippet>
            {Array.isArray(section.content) ? (
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {section.content.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            ) : typeof section.content === 'object' ? (
              <CodeText>{JSON.stringify(section.content, null, 2)}</CodeText>
            ) : (
              <p style={{ margin: 0 }}>{section.content}</p>
            )}
          </PreviewSnippet>
        </SectionMain>
      </SectionCardContent>
    </SectionWrapper>
  );
};

const VisualPromptBuilder = ({ value, onChange }) => {
  const [sections, setSections] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const addSection = (libraryKey) => {
    const template = SECTION_LIBRARY[libraryKey];
    const newSection = {
      ...template,
      id: `${template.type}_${Date.now()}`
    };
    setSections([...sections, newSection]);
    updateParent([...sections, newSection]);
    message.success(`${template.name} INTEGRATED`);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(items, oldIndex, newIndex);
        updateParent(reordered);
        return reordered;
      });
    }
  };

  const deleteSection = (id) => {
    const updated = sections.filter((s) => s.id !== id);
    setSections(updated);
    updateParent(updated);
  };

  const duplicateSection = (section) => {
    const duplicate = { ...section, id: `${section.type}_${Date.now()}`, name: `${section.name} (Clone)` };
    setSections([...sections, duplicate]);
    updateParent([...sections, duplicate]);
  };

  const openEditModal = (section) => {
    setEditingSection({ ...section });
    setEditModalVisible(true);
  };

  const saveEdit = () => {
    const updated = sections.map((s) => s.id === editingSection.id ? editingSection : s);
    setSections(updated);
    updateParent(updated);
    setEditModalVisible(false);
  };

  const updateParent = (sectionsList) => {
    const promptDef = {
      pipeline: sectionsList.map((s) => ({
        stage: s.type,
        definition: s.content
      }))
    };
    onChange?.(promptDef);
  };

  const generatePreview = () => {
    return sections.map(s => `[${s.name.toUpperCase()}]\n${typeof s.content === 'object' ? JSON.stringify(s.content, null, 2) : s.content}`).join('\n\n');
  };

  return (
    <BuilderContainer>
      <LibrarySidebar>
        <SidebarHeader>
          <AppstoreAddOutlined /> COMPONENT HUB
        </SidebarHeader>
        <LibraryList>
          {Object.entries(SECTION_LIBRARY).map(([key, section]) => (
            <LibraryCard key={key} onClick={() => addSection(key)}>
              <div style={{ fontSize: 20 }}>{section.icon}</div>
              <div>
                <div style={{ fontWeight: 800, fontSize: 10, color: 'var(--aura-navy)' }}>{section.name.toUpperCase()}</div>
                <div style={{ fontSize: 9, color: 'var(--aura-slate)', fontWeight: 600 }}>{section.description}</div>
              </div>
            </LibraryCard>
          ))}
        </LibraryList>
        
        <StatusMonitor>
          <MonitorItem>
            <ThunderboltFilled style={{ color: '#64ffda' }} />
            <span>PIPELINE: ACTIVE</span>
          </MonitorItem>
          <MonitorItem>
            <SafetyCertificateFilled style={{ color: 'var(--aura-blue)' }} />
          </MonitorItem>
        </StatusMonitor>
      </LibrarySidebar>

      <MainCanvas>
        <CanvasHeader>
          <CanvasTitle>
            <CodeFilled style={{ marginRight: 10, color: 'var(--aura-blue)' }} />
            CONSTRUCTING INTELLIGENCE PIPELINE ({sections.length} STAGES)
          </CanvasTitle>
          <Space>
            <ActionButton icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)}>PREVIEW</ActionButton>
          </Space>
        </CanvasHeader>

        <DropZone>
          {sections.length === 0 ? (
            <EmptyCanvas>
              <EmptyOrb />
              <div style={{ color: 'var(--aura-navy)', fontWeight: 800, marginTop: 16, fontSize: 12 }}>PIPELINE EMPTY</div>
              <Text style={{ fontSize: 10, color: 'var(--aura-slate)' }}>Inject components from the Hub.</Text>
            </EmptyCanvas>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                <PipelineFlow>
                  {sections.map(section => (
                    <SortableSection
                      key={section.id}
                      section={section}
                      onEdit={openEditModal}
                      onDelete={deleteSection}
                      onDuplicate={duplicateSection}
                    />
                  ))}
                </PipelineFlow>
              </SortableContext>
            </DndContext>
          )}
        </DropZone>
      </MainCanvas>

      <Modal
        title="CALIBRATE COMPONENT"
        open={editModalVisible}
        onOk={saveEdit}
        onCancel={() => setEditModalVisible(false)}
        width={500}
        centered
        className="aura-modal-compact"
      >
        {editingSection && (
          <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
            <div>
              <Text strong style={{ fontSize: 10, letterSpacing: 1, color: 'var(--aura-slate)' }}>IDENTIFIER</Text>
              <Input 
                value={editingSection.name} 
                onChange={e => setEditingSection({...editingSection, name: e.target.value})} 
                style={{ marginTop: 6, borderRadius: 6 }}
              />
            </div>
            <div>
              <Text strong style={{ fontSize: 10, letterSpacing: 1, color: 'var(--aura-slate)' }}>LOGIC DEFINITION</Text>
              <TextArea
                rows={6}
                value={typeof editingSection.content === 'string' ? editingSection.content : JSON.stringify(editingSection.content, null, 2)}
                onChange={e => {
                  let val = e.target.value;
                  try { val = JSON.parse(val); } catch {}
                  setEditingSection({...editingSection, content: val});
                }}
                style={{ marginTop: 6, borderRadius: 6, fontFamily: 'monospace', fontSize: 12 }}
              />
            </div>
          </Space>
        )}
      </Modal>

      <Modal
        title="SYSTEM PREVIEW"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={700}
        centered
      >
        <GlassPreview>
          <pre>{generatePreview()}</pre>
        </GlassPreview>
      </Modal>
    </BuilderContainer>
  );
};

const BuilderContainer = styled.div`
  display: flex;
  gap: 20px;
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  min-height: 600px;
  border: 1px solid #f1f5f9;
  font-family: 'Inter', sans-serif;
`;

const LibrarySidebar = styled.div`
  width: 240px;
  background: white;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  font-size: 9px;
  font-weight: 850;
  letter-spacing: 1px;
  color: var(--aura-slate);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LibraryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const LibraryCard = styled.div`
  background: #f8fafc;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid transparent;
  
  &:hover {
    background: white;
    border-color: #e2e8f0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.03);
    transform: translateY(-1px);
  }
`;

const MainCanvas = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CanvasHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
`;

const CanvasTitle = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: var(--aura-navy);
`;

const DropZone = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #f1f5f9;
  overflow-y: auto;
`;

const PipelineFlow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionWrapper = styled.div`
  background: ${props => props.$isDragging ? '#f0f9ff' : 'white'};
  border: 1px solid ${props => props.$isDragging ? 'var(--aura-blue)' : '#f1f5f9'};
  border-radius: 8px;
  display: flex;
  padding: 12px;
  transition: all 0.2s;
  
  &:hover {
    border-color: #e2e8f0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
  }
`;

const DragHandle = styled.div`
  cursor: grab;
  color: #cbd5e1;
  padding: 0 8px;
  display: flex;
  align-items: center;
  &:hover { color: var(--aura-navy); }
`;

const SectionCardContent = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
`;

const SectionBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const SectionMain = styled.div`
  flex: 1;
`;

const SectionHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const SectionTitleText = styled.div`
  font-weight: 800;
  font-size: 12px;
  color: var(--aura-navy);
`;

const SectionSubtext = styled.div`
  font-size: 9px;
  font-weight: 600;
  color: var(--aura-slate);
`;

const PreviewSnippet = styled.div`
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  color: var(--aura-slate);
`;

const CodeText = styled.pre`
  margin: 0;
  font-family: 'Fira Code', monospace;
  font-size: 11px;
`;

const IconButton = styled(Button)`
  border: none !important;
  background: transparent !important;
  color: var(--aura-slate) !important;
  width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  &:hover { background: #f1f5f9 !important; color: var(--aura-navy) !important; }
`;

const ActionIcons = styled(Space)`
  opacity: 0;
  transition: opacity 0.2s;
  ${SectionWrapper}:hover & { opacity: 1; }
`;

const EmptyCanvas = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-bottom: 40px;
`;

const EmptyOrb = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3a86ff, #0a192f);
  border-radius: 50%;
  filter: blur(24px);
  opacity: 0.3;
`;

const ActionButton = styled(Button)`
  border-radius: 6px !important;
  font-weight: 800 !important;
  font-size: 10px !important;
  height: 32px !important;
`;

const StatusMonitor = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MonitorItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 9px;
  font-weight: 900;
  color: var(--aura-slate);
`;

const GlassPreview = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 16px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #f1f5f9;
  
  pre {
    margin: 0;
    font-size: 12px;
    line-height: 1.6;
    color: var(--aura-navy);
    font-family: 'Fira Code', monospace;
  }
`;

export default VisualPromptBuilder;
